import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { SelectNative } from "../components/ui/select-native";
import { useAuth } from "../context/AuthContext";
import { useProfile } from "../context/ProfileContext";
import { INCOME_RANGES } from "../lib/profileOptions";
import { deleteAccount } from "../lib/api";
import { normalizeDetail } from "../lib/errors";

const goalSchema = z.object({
  name: z.string().min(1, "Required"),
  target_amount: z.coerce.number({ invalid_type_error: "Required" }).positive("Must be greater than 0"),
  target_year: z.coerce
    .number({ invalid_type_error: "Required" })
    .int()
    .min(2000, "Enter a valid year")
    .max(2200, "Enter a valid year"),
});

const profileSchema = z.object({
  name: z.string().min(1, "Required"),
  mobile: z.string().min(1, "Required"),
  pan: z
    .string()
    .trim()
    .toUpperCase()
    .refine((v) => v === "" || /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v), "PAN must match AAAAA9999A")
    .optional()
    .or(z.literal("")),
  monthly_income_inr: z
    .union([
      z.literal(""),
      z.coerce.number({ invalid_type_error: "Must be a number" }).min(0, "Cannot be negative"),
    ])
    .optional(),
  income_range: z.enum(INCOME_RANGES.map((r) => r.value)),
  approx_net_worth: z.coerce.number({ invalid_type_error: "Required" }).min(0, "Cannot be negative"),
  short_term_goals: z.array(goalSchema),
  long_term_goals: z.array(goalSchema),
  consent: z.boolean().refine((v) => v === true, "You must agree before saving your financial profile."),
});

const BLANK_GOAL = { name: "", target_amount: "", target_year: new Date().getFullYear() + 1 };

function emptyDefaults() {
  return {
    name: "",
    mobile: "",
    pan: "",
    monthly_income_inr: "",
    income_range: INCOME_RANGES[0].value,
    approx_net_worth: "",
    short_term_goals: [],
    long_term_goals: [],
    consent: false,
  };
}

function profileToFormValues(profile) {
  return {
    name: profile.name,
    mobile: profile.mobile,
    pan: profile.pan || "",
    monthly_income_inr: profile.monthly_income_inr ?? "",
    income_range: profile.income_range,
    approx_net_worth: profile.approx_net_worth,
    short_term_goals: profile.short_term_goals,
    long_term_goals: profile.long_term_goals,
    consent: profile.consent_given,
  };
}

function GoalFieldArray({ control, register, errors, name, addLabel }) {
  const { fields, append, remove } = useFieldArray({ control, name });
  return (
    <div className="flex flex-col gap-3">
      {fields.map((field, index) => (
        <div key={field.id} className="grid grid-cols-[1fr_1fr_auto_auto] items-end gap-2 rounded-md border border-border p-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${name}.${index}.name`}>Goal</Label>
            <Input id={`${name}.${index}.name`} {...register(`${name}.${index}.name`)} placeholder="e.g. Child's education" />
            {errors?.[index]?.name && <p className="text-xs text-red-400">{errors[index].name.message}</p>}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${name}.${index}.target_amount`}>Target amount (₹)</Label>
            <Input id={`${name}.${index}.target_amount`} type="number" step="any" {...register(`${name}.${index}.target_amount`)} />
            {errors?.[index]?.target_amount && <p className="text-xs text-red-400">{errors[index].target_amount.message}</p>}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${name}.${index}.target_year`}>Target year</Label>
            <Input id={`${name}.${index}.target_year`} type="number" step="1" className="w-24" {...register(`${name}.${index}.target_year`)} />
            {errors?.[index]?.target_year && <p className="text-xs text-red-400">{errors[index].target_year.message}</p>}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => remove(index)}>
            Remove
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="self-start" onClick={() => append(BLANK_GOAL)}>
        {addLabel}
      </Button>
    </div>
  );
}

/**
 * Member profile (Task 10): name, email (read-only, from the account), mobile, income band,
 * approx net worth, and short/long-term goals — all encrypted server-side. Consent must be
 * ticked before any of it is saved (CLAUDE.md "consent before storing financial data").
 */
export function ProfilePage() {
  const { member, refresh: refreshAuth } = useAuth();
  const { profile, loading, save, remove } = useProfile();
  const navigate = useNavigate();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: emptyDefaults(),
  });

  useEffect(() => {
    if (profile) reset(profileToFormValues(profile));
  }, [profile, reset]);

  async function onSubmit(values) {
    try {
      // Optional PII fields — normalise "" → null so the backend Pydantic model gets the right
      // shape (empty string can't be coerced to float for monthly_income_inr).
      const payload = {
        ...values,
        pan: values.pan ? values.pan.trim().toUpperCase() : null,
        monthly_income_inr:
          values.monthly_income_inr === "" || values.monthly_income_inr === undefined
            ? null
            : Number(values.monthly_income_inr),
      };
      await save(payload);
      toast.success("Profile saved.");
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't save your profile."));
    }
  }

  async function handleDeleteProfile() {
    if (!window.confirm("Delete your profile data (name, mobile, income, net worth, goals)? This can't be undone.")) return;
    await remove();
    reset(emptyDefaults());
    toast.success("Profile deleted.");
  }

  async function handleDeleteAccount() {
    if (!window.confirm("Delete your account and all data (profile + saved scenarios)? This can't be undone.")) return;
    await deleteAccount();
    await refreshAuth();
    navigate("/login", { replace: true });
    toast.success("Your account and data have been deleted.");
  }

  if (loading) return null;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <h1 className="text-2xl font-semibold text-text">My Profile</h1>
      <p className="mt-1 text-sm text-muted">Used to pre-fill calculators and personalise your saved scenarios.</p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Contact details</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="name">Full name</Label>
              <Input id="name" {...register("name")} />
              {errors.name && <p className="text-xs text-red-400">{errors.name.message}</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Email</Label>
              <Input value={member?.email || "—"} disabled />
              <p className="text-xs text-muted">Your login email — can't be changed here.</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mobile">Mobile</Label>
              <Controller
                control={control}
                name="mobile"
                render={({ field }) => (
                  <PhoneInput
                    id="mobile"
                    international
                    defaultCountry="IN"
                    value={field.value}
                    onChange={field.onChange}
                    inputComponent={Input}
                    className="phone-input-theme"
                  />
                )}
              />
              {errors.mobile && <p className="text-xs text-red-400">{errors.mobile.message}</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pan">PAN <span className="text-xs text-muted">(optional)</span></Label>
              <Input
                id="pan"
                data-testid="profile-pan"
                {...register("pan")}
                placeholder="AAAAA9999A"
                maxLength={10}
                className="uppercase tracking-widest"
              />
              {errors.pan && <p className="text-xs text-red-400">{errors.pan.message}</p>}
              <p className="text-xs text-muted">
                Encrypted at rest. Used only to pre-fill tax calculators — never shared.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Financial snapshot</CardTitle>
            <CardDescription>Used only to pre-fill calculators — never shown to anyone else.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="monthly_income_inr">
                Current monthly income (₹) <span className="text-xs text-muted">(optional)</span>
              </Label>
              <Input
                id="monthly_income_inr"
                data-testid="profile-monthly-income"
                type="number"
                step="1000"
                min="0"
                {...register("monthly_income_inr")}
                placeholder="e.g. 250000"
              />
              {errors.monthly_income_inr && (
                <p className="text-xs text-red-400">{errors.monthly_income_inr.message}</p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="income_range">Annual income band</Label>
              <SelectNative id="income_range" {...register("income_range")}>
                {INCOME_RANGES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </SelectNative>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="approx_net_worth">Approx. net worth (₹)</Label>
              <Input id="approx_net_worth" type="number" step="any" {...register("approx_net_worth")} />
              {errors.approx_net_worth && <p className="text-xs text-red-400">{errors.approx_net_worth.message}</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Short-term goals</CardTitle>
            <CardDescription>Within the next few years.</CardDescription>
          </CardHeader>
          <CardContent>
            <GoalFieldArray control={control} register={register} errors={errors.short_term_goals} name="short_term_goals" addLabel="+ Add short-term goal" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Long-term goals</CardTitle>
            <CardDescription>Further out — retirement, a child's education, etc.</CardDescription>
          </CardHeader>
          <CardContent>
            <GoalFieldArray control={control} register={register} errors={errors.long_term_goals} name="long_term_goals" addLabel="+ Add long-term goal" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-3 pt-6">
            <label className="flex items-start gap-2 text-sm text-text">
              <input type="checkbox" className="mt-1" {...register("consent")} />
              <span>
                I consent to The Money Gurukul storing my financial information (PAN, income, net worth, and
                goals) in encrypted form, used only to personalise calculators and saved scenarios for my
                own use. I can edit or delete this data, or my account, at any time.
              </span>
            </label>
            {errors.consent && <p className="text-xs text-red-400">{errors.consent.message}</p>}
          </CardContent>
          <CardFooter className="flex flex-wrap gap-2">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving…" : profile ? "Save changes" : "Save profile"}
            </Button>
            {profile && (
              <Button type="button" variant="outline" onClick={handleDeleteProfile}>
                Delete profile data
              </Button>
            )}
            <Button type="button" variant="outline" className="ml-auto text-red-400" onClick={handleDeleteAccount}>
              Delete account
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
